<template>
    <div
        class="auth-promo-slider relative xl:pb-6"
        role="region"
        aria-roledescription="carousel"
        aria-label="My10x features"
        aria-live="polite"
        tabindex="0"
        @keydown="onKeydown"
    >
        <Carousel
            ref="carouselRef"
            :autoplay="5000"
            :wrap-around="true"
            :pause-autoplay-on-hover="false"
            :mouse-drag="false"
            :touch-drag="false"
            :modelValue="currentSlide"
            :items-to-show="1"
            @update:modelValue="onSlideUpdate"
        >
            <Slide
                v-for="(s, idx) in slides"
                :key="s.id ?? idx"
                :aria-label="`Slide ${idx + 1} of ${slides.length}`"
            >
                <section
                    class="relative flex flex-col items-start w-full  gap-10 px-6 md:px-0"
                >
                    <figure
                        class="w-full rounded-xl overflow-visible"
                    >
                        <img
                            :src="s.img"
                            :alt="s.alt"
                            :srcset="s.srcset"
                            :loading="idx === 0 ? 'eager' : 'lazy'"
                            class="block w-full h-auto max-h-[50vh] object-contain object-left"
                        />
                    </figure>

                    <div class="w-full">
                        <div
                            class="flex flex-col justify-end items-start gap-3 md:gap-4 w-full px-6 md:px-0 pt-2 pb-2"
                        >
                            <!-- md:max-w-[535px] -->
                            <div
                                class="flex flex-col items-start gap-3 w-full md:pr-8 lg:pr-2"
                            >
                                <h3
                                    class="text-slate-900 text-[30px] leading-9 font-semibold"
                                    :class="
                                        s.titleNarrow ? 'max-w-[287px]' : ''
                                    "
                                >
                                    <template v-if="s.titleParts">
                                        {{ s.titleParts.before }}
                                        <span
                                            class="text-[#FD421D] whitespace-nowrap"
                                        >
                                            {{ s.titleParts.highlight }}
                                        </span>
                                        <span v-if="s.titleParts.after">
                                            {{ s.titleParts.after }}
                                        </span>
                                    </template>
                                    <template v-else>
                                        {{ s.title }}
                                    </template>
                                </h3>
                                <p class="text-slate-500 text-sm leading-5">
                                    {{ s.subtitle }}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            </Slide>
        </Carousel>
        <div class="w-full md:px-0 px-6">
            <div class="flex items-center justify-between w-full py-1 px-1">
                <!-- md:max-w-[535px] -->
                <!-- removed mx-auto -->
                <div class="flex items-center gap-2 relative z-10">
                    <button
                        v-for="(seg, i) in slides"
                        :key="`seg-${i}`"
                        type="button"
                        class="w-6 h-1 rounded-full"
                        :class="
                            i === currentSlide ? 'bg-orange' : 'bg-orange-100'
                        "
                        :aria-label="`Go to slide ${i + 1}`"
                        :aria-selected="i === currentSlide"
                        @click.stop.prevent="goTo(i)"
                    ></button>
                </div>
                <div class="flex items-center gap-2">
                    <button
                        type="button"
                        class="inline-flex items-center justify-center w-9 h-9 rounded-full text-[#FD421D] hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-300"
                        aria-label="Previous slide"
                        @click.stop.prevent="prev"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            class="w-4 h-4"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M15 18l-6-6 6-6"
                            />
                        </svg>
                    </button>
                    <button
                        type="button"
                        class="inline-flex items-center justify-center w-9 h-9 rounded-full text-[#FD421D] hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-300"
                        aria-label="Next slide"
                        @click.stop.prevent="next"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            class="w-4 h-4"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M9 6l6 6-6 6"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
        <!-- Fixed mobile CTA (outside slides) -->
        <div class="w-full md:hidden flex px-6 justify-center items-end gap-2">
            <div class="flex w-full items-center gap-4 pt-4">
                <button
                    type="button"
                    class="inline-flex items-center justify-center flex-1 px-4 py-2.5 rounded border border-slate-200 bg-white dark:bg-slate-800 text-slate-800 text-[14px] leading-5 font-medium shadow-sm"
                    @click.stop.prevent="goLogin"
                >
                    Sign In
                </button>
                <button
                    type="button"
                    class="inline-flex items-center justify-center flex-1 px-4 py-2.5 rounded bg-[#FD421D] text-white text-[14px] leading-5 font-medium shadow-sm"
                    @click.stop.prevent="goRegister"
                >
                    Sign Up
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { router } from '@inertiajs/vue3';
import { Carousel, Slide } from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';

const carouselRef = ref(null);
const currentSlide = ref(0);

const loaded = reactive(new Set());
const isLoaded = (i) => loaded.has(i);
const onImgLoad = (i) => {
    loaded.add(i);
    nextTick(reportFigureHeight);
};

const slides = [
    {
        id: 'wallet',
        title: 'Track all your cards in one place',
        titleParts: {
            before: 'Track all your cards in',
            highlight: 'one place',
            after: '',
        },
        titleNarrow: true,
        subtitle:
            'Add and manage all your cards in your private wallet. Explore and track card benefits, receive personalized recommendations, and more.',
        img: '/images/register-slider/my-wallet-full.png',
        alt: 'Wallet view with card list',
        srcset: '/images/register-slider/my-wallet-full.png 1x',
    },
    {
        id: 'course',
        title: 'Become a travel rewards genius',
        titleParts: {
            before: 'Become a travel',
            highlight: 'rewards genius',
            after: '',
        },
        titleNarrow: true,
        subtitle:
            'Join our free course to learn a step-by-step guide on how to travel the world for next to nothing.',
        img: '/images/register-slider/10x-course-full.png',
        alt: '10x Takeoff course overview',
        srcset: '/images/register-slider/10x-course-full.png 1x',
    },
    {
        id: 'points',
        title: 'Keep track of all your rewards',
        titleParts: {
            before: 'Keep track of',
            highlight: 'all your rewards',
            after: '',
        },
        titleNarrow: true,
        subtitle:
            "You can monitor all your loyalty balances in one place. Link accounts for automatic updates or add them yourself, and we will notify you when it's time to use them.",
        img: '/images/register-slider/my-points-full.png',
        alt: 'My Points balances dashboard',
        srcset: '/images/register-slider/my-points-full.png 1x',
    },
    {
        id: 'recommendations',
        title: 'Best cards, made for you',
        titleParts: {
            before: 'Best cards, made',
            highlight: 'for you',
            after: '',
        },
        titleNarrow: true,
        subtitle:
            'With one click, our tool reviews your cards, rewards, and eligibility to recommend your ideal next card.',
        img: '/images/register-slider/my-next-card-full.png',
        alt: 'Best card recommendations preview',
        srcset: '/images/register-slider/my-next-card-full.png 1x',
    },
    {
        id: 'redemptions',
        title: 'Turn your points into free trips',
        titleParts: {
            before: 'Turn your points',
            highlight: 'into free trips',
            after: '',
        },
        titleNarrow: true,
        subtitle:
            'Plan trips, track your redemptions for flights, hotels, car rentals, and more. All in one easy view.',
        img: '/images/register-slider/redemption-full.png',
        alt: 'Trip redemptions planning overview',
        srcset: '/images/register-slider/redemption-full.png 1x',
    },
];

const onSlideUpdate = (val) => {
    currentSlide.value = val;
    nextTick(reportFigureHeight);
};
const prev = () => {
    const t = slides.length;
    currentSlide.value = (currentSlide.value - 1 + t) % t;
};
const next = () => {
    const t = slides.length;
    currentSlide.value = (currentSlide.value + 1) % t;
};
const goTo = (i) => {
    currentSlide.value = i;
};

const onKeydown = (e) => {
    if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
    } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
    } else if (e.key === 'Home') {
        e.preventDefault();
        goTo(0);
    } else if (e.key === 'End') {
        e.preventDefault();
        goTo(slides.length - 1);
    }
};

function onImgError(i) {
    loaded.delete(i);
}

function goLogin() {
    try {
        router.visit('/login?form=1');
    } catch (_) {}
}
function goRegister() {
    try {
        router.visit('/register?form=1');
    } catch (_) {}
}

function reportFigureHeight() {
    try {
        const root = document.querySelector('.auth-promo-slider');
        const visible =
            root?.querySelector('.carousel__slide--visible') ||
            root?.querySelector('.carousel__slide[aria-hidden="false"]');
        const fig = visible?.querySelector('figure');
        if (!fig) return;
        const rect = fig.getBoundingClientRect();
        const h = Math.round(rect.height || 0);
        if (h > 0) {
            window.dispatchEvent(
                new CustomEvent('mobile-slider-figure-height', {
                    detail: { height: h },
                })
            );
        }
    } catch (_) {}
}

onMounted(() => {
    try {
        reportFigureHeight();
    } catch (_) {}
    const onR = () => reportFigureHeight();
    window.addEventListener('resize', onR, { passive: true });
    reportFigureHeight._resizeH = onR;
});

onBeforeUnmount(() => {
    try {
        if (reportFigureHeight._resizeH)
            window.removeEventListener('resize', reportFigureHeight._resizeH);
    } catch (_) {}
});
</script>

<style scoped>
.auth-promo-slider :deep(.carousel__slide) {
    padding: 0 !important;
}
.auth-promo-slider :deep(.carousel__pagination-button) {
    outline: none;
}
.auth-promo-slider :deep(.carousel__pagination-button:focus-visible) {
    box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.45);
    border-radius: 9999px;
}
.auth-promo-slider :deep(.carousel__prev),
.auth-promo-slider :deep(.carousel__next) {
    background: #fff;
    border: 1px solid rgba(15, 23, 42, 0.08);
    color: #0f172a;
}
.auth-promo-slider :deep(.carousel__prev:focus-visible),
.auth-promo-slider :deep(.carousel__next:focus-visible) {
    box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.45);
}
/* Prevent overlapping descriptions around md (~768-800px):
   hide non-visible slides' content using aria-hidden set by vue3-carousel. */
@media (min-width: 768px) {
    .auth-promo-slider :deep(.carousel__slide[aria-hidden='true']) {
        visibility: hidden;
    }
}
</style>
